import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetAssessmentsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  useGetAssessmentsRegister,
  type UseGetAssessmentsRegisterArgs,
} from 'src/hooks/queries/assessment/useGetAssessmentsRegister';
import { useGetAssessmentSmartWidgetTableProps } from 'src/pages/assessments/config';
import type { AssessmentRegisterFields } from 'src/pages/assessments/types';

import { assessmentRegisterUrl } from '@/utils/urls';

import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  dateRangeClickthroughFilter,
  defaultClickthroughFilterWithUnrated,
  tagAndDepartmentCategoryGetters,
} from '../dataSourceHelpers';

export default createDataSource<
  AssessmentRegisterFields,
  UseGetAssessmentsRegisterArgs,
  GetAssessmentsQuery
>({
  group: 'single',
  hasAccess: () => true,
  useWidgetQuery: useGetAssessmentsRegister,
  parentTypes: [Parent_Type_Enum.Assessment],
  useTablePropsHook: (data, options) =>
    useGetAssessmentSmartWidgetTableProps(data?.assessment, options),
  entityNamePlural: 'assessment_other',
  entityNameSingular: 'assessment_one',
  fields: 'assessments.fields',
  dashboardFilterConfig: {
    tagsFilter: (tags) => ({ tagTypeIds: tags }),
    departmentsFilter: (departments) => ({ departmentTypeIds: departments }),
    dateFilter: (dateRange, precision) => ({
      dateRange: {
        field: 'CreatedAtTimestamp',
        ...toIsoBounds(dateRange, precision),
      },
    }),
    ownershipFilter: (myItemsFilters) => ({ ownership: myItemsFilters }),
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
  },
  clickThroughUrl: (filters) => assessmentRegisterUrl(filters),
  categoryGetters: [
    ...tagAndDepartmentCategoryGetters<AssessmentRegisterFields>(),
    {
      id: 'status',
      name: () => i18n.t('assessments.columns.Status'),
      categoryGetter: (item) => ({
        key: item.Status,
        label: item.StatusLabelled,
      }),
      ratingColourKey: 'assessment_status',
      clickthroughFilter:
        defaultClickthroughFilterWithUnrated('StatusLabelled'),
    },
    {
      id: 'owner',
      fieldId: 'Owners',
      name: () => i18n.t('assessments.columns.Owner'),
      categoryGetter: (item) =>
        item.allOwners.map((owner) => ({ key: owner.id, label: owner.label })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'contributor',
      fieldId: 'Contributors',
      name: () => i18n.t('assessments.columns.contributor'),
      categoryGetter: (item) =>
        item.allContributors.map((contributor) => ({
          key: contributor.id,
          label: contributor.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allContributors',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'completionDate',
      fieldId: 'ActualCompletionDate',
      name: () => i18n.t('assessments.columns.CompletionDate'),
      categoryGetter: (data) =>
        data.ActualCompletionDate ? new Date(data.ActualCompletionDate) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'ActualCompletionDate',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('ActualCompletionDate'),
    },
    {
      id: 'startDate',
      fieldId: 'StartDate',
      name: () => i18n.t('assessments.columns.StartDate'),
      categoryGetter: (data) =>
        data.StartDate ? new Date(data.StartDate) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: { field: 'StartDate', ...toIsoBounds(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('StartDate'),
    },
    {
      id: 'createdDate',
      name: () => i18n.t('columns.created_on'),
      categoryGetter: (data) => new Date(data.CreatedAtTimestamp),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'CreatedAtTimestamp',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('CreatedAtTimestamp'),
    },
  ],
});
